import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  saveTutorial,
  unsaveTutorial,
  getUserSavedTutorials,
} from "@/lib/supabase";
import { fetchTutorialsByIds, type Tutorial } from "@/lib/api";

export function useSavedTutorials(userId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: savedTutorialIds = [], isLoading: isLoadingIds } = useQuery({
    queryKey: ["savedTutorials", userId],
    queryFn: () => getUserSavedTutorials(userId!),
    enabled: !!userId,
  });

  const { data: savedTutorials = [], isLoading: isLoadingTutorials } = useQuery(
    {
      queryKey: ["savedTutorials", userId, "details"],
      queryFn: () => fetchTutorialsByIds(savedTutorialIds),
      enabled: !!userId && savedTutorialIds.length > 0,
    }
  );

  const toggleSavedTutorial = useMutation({
    mutationFn: async ({
      tutorialId,
      isSaved,
    }: {
      tutorialId: string;
      isSaved: boolean;
    }) => {
      if (!userId) throw new Error("User must be logged in");

      if (isSaved) {
        await unsaveTutorial(userId, tutorialId);
      } else {
        await saveTutorial(userId, tutorialId);
      }
    },
    onMutate: async ({ tutorialId, isSaved }) => {
      await queryClient.cancelQueries({
        queryKey: ["savedTutorials", userId],
      });

      const previousTutorials = queryClient.getQueryData<string[]>([
        "savedTutorials",
        userId,
      ]);

      if (!previousTutorials) return { previousTutorials };

      const newTutorials = isSaved
        ? previousTutorials.filter((id) => id !== tutorialId)
        : [...previousTutorials, tutorialId];

      queryClient.setQueryData(["savedTutorials", userId], newTutorials);

      return { previousTutorials };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousTutorials) {
        queryClient.setQueryData(
          ["savedTutorials", userId],
          context.previousTutorials
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["savedTutorials", userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["tutorials", "user", userId],
      });
    },
  });

  const isTutorialSaved = (tutorialId: string): boolean => {
    return savedTutorialIds.includes(tutorialId);
  };

  return {
    savedTutorials,
    isTutorialSaved,
    isLoading: isLoadingIds || isLoadingTutorials,
    toggleSavedTutorial: (tutorialId: string) =>
      toggleSavedTutorial.mutate({
        tutorialId,
        isSaved: isTutorialSaved(tutorialId),
      }),
  };
}
